// ============================================================================
// FSA NOTE MODAL — DEPRECIATION CALC CROSS-CHECK STRIP  (ADR-0006 Phase 1b)
// ============================================================================
// Surfaces the saved Depreciation Calculation's movement figures inside the
// PPE / Intangible note as an INFORMATIONAL working-paper cross-check —
// nothing here can modify the note workspace (rev 4: the earlier per-class
// Apply was removed because applying dirtied the note and blocked print
// preview until a save; the auditor enters figures directly in the grid).
//
//   • all matching → subtle green "matches Depreciation Calc" line
//   • differences  → neutral info strip with an expandable per-cell
//     comparison table (note value vs calc value). Never blocks anything.
//
// A second export, DepCalcDtStrip, renders on the two Deferred Tax notes
// with the calc's DT position / charge as the same kind of cross-check.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fsaDepreciationCalcService } from 'api/services/fsa/fsaDepreciationCalcService';
import type { ID } from 'api/types';
import type { FormatColumnSpec } from 'api/types/fsa/template.types';
import type { DepDtSuggestion, DepNoteSuggestion } from 'api/types/fsa/depreciationCalc.types';
import type { NoteWorkspaceData } from 'api/types/fsa/noteData.types';

const EPSILON = 0.005;

const fmt = (v: number): string =>
  v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Auditor presentation — negative values in brackets. */
const fmtBracket = (v: number): string => (v < 0 ? `(${fmt(Math.abs(v))})` : fmt(v));

// ---------------------------------------------------------------------------
// Movement cross-check — PPE / Intangibles
// ---------------------------------------------------------------------------

interface StripProps {
  engagementId: ID;
  scheduleId: ID;
  workspace: NoteWorkspaceData;
  columnSpec: FormatColumnSpec[];
  /** class code (or custom local_id) → display name. */
  classNames: Record<string, string>;
}

export const DepCalcSuggestionStrip: React.FC<StripProps> = ({
  engagementId, scheduleId, workspace, columnSpec, classNames,
}) => {
  const [suggestion, setSuggestion] = useState<DepNoteSuggestion | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fsaDepreciationCalcService.getNoteSuggestions(engagementId, scheduleId);
        if (!cancelled) setSuggestion(r.data ?? null);
      } catch {
        // The cross-check is an overlay — a fetch failure must never break the note.
        if (!cancelled) setSuggestion(null);
      }
    })();
    return () => { cancelled = true; };
  }, [engagementId, scheduleId]);

  const colLabel = useCallback((code: string): string =>
    columnSpec.find(c => c.code === code)?.label ?? code,
  [columnSpec]);

  const clsName = useCallback((code: string): string => classNames[code] ?? code, [classNames]);

  /** Per-cell comparison against the CURRENT workspace (reactive — clears as the auditor enters figures). */
  const diffs = useMemo(() => {
    if (!suggestion?.available) return [];
    const out: Array<{ classCode: string; colCode: string; noteValue: number; calcValue: number }> = [];
    for (const [classCode, cols] of Object.entries(suggestion.classValues)) {
      const cd = workspace.class_data?.[classCode];
      for (const [colCode, calcValue] of Object.entries(cols)) {
        const noteValue = cd?.column_values?.[colCode] ?? 0;
        if (Math.abs(noteValue - calcValue) > EPSILON) {
          out.push({ classCode, colCode, noteValue, calcValue });
        }
      }
    }
    return out;
  }, [suggestion, workspace.class_data]);

  if (!suggestion?.available || Object.keys(suggestion.classValues).length === 0) return null;

  if (diffs.length === 0) {
    return (
      <div style={{ ...stripStyle, background: 'rgba(22,163,74,0.08)', borderColor: 'rgba(22,163,74,0.35)' }}>
        <span style={{ color: '#16a34a', fontWeight: 600 }}>✓</span>
        <span style={{ color: 'var(--text-secondary)' }}>
          Movement columns match the Depreciation Calc working paper.
        </span>
      </div>
    );
  }

  return (
    <div style={{ ...stripStyle, flexDirection: 'column', alignItems: 'stretch' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>ℹ</span>
        <span style={{ color: 'var(--text-primary)' }}>
          Depreciation Calc working paper: <b>{diffs.length}</b> movement
          cell{diffs.length === 1 ? '' : 's'} differ{diffs.length === 1 ? 's' : ''} from this note.
        </span>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>
          Informational cross-check only — enter figures in the grid as you see fit.
        </span>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Hide comparison' : 'Compare'}
        </button>
      </div>
      {expanded && (
        <div style={{ marginTop: 8, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
            <thead>
              <tr>
                {['Class', 'Column', 'Note value', 'Calc value'].map((h, i) => (
                  <th key={h} style={{ ...diffThStyle, textAlign: i >= 2 ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {diffs.map(d => (
                <tr key={`${d.classCode}:${d.colCode}`}>
                  <td style={diffTdStyle}>{clsName(d.classCode)}</td>
                  <td style={diffTdStyle}>{colLabel(d.colCode)}</td>
                  <td style={{ ...diffTdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(d.noteValue)}</td>
                  <td style={{ ...diffTdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt(d.calcValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Deferred tax — informational comparison strip
// ---------------------------------------------------------------------------

interface DtStripProps {
  engagementId: ID;
  /** NOTE_DEFERRED_TAX (BS position) or NOTE_DEFERRED_TAX_EXPENSE (P&L charge). */
  scheduleCode: string;
  workspace: NoteWorkspaceData;
}

export const DepCalcDtStrip: React.FC<DtStripProps> = ({ engagementId, scheduleCode, workspace }) => {
  const [suggestion, setSuggestion] = useState<DepDtSuggestion | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fsaDepreciationCalcService.getDtSuggestion(engagementId);
        if (!cancelled) setSuggestion(r.data ?? null);
      } catch {
        if (!cancelled) setSuggestion(null);
      }
    })();
    return () => { cancelled = true; };
  }, [engagementId]);

  if (!suggestion?.available) return null;

  const isExpenseNote = scheduleCode === 'NOTE_DEFERRED_TAX_EXPENSE';
  const calcValue = isExpenseNote
    ? (suggestion.chargeForYearRounded ?? suggestion.chargeForYearActual)
    : (suggestion.closingPositionRounded ?? suggestion.closingPositionActual);
  if (calcValue == null) return null;

  // The DT position class the calc's figure corresponds to (BS note only).
  const depDiffClass = workspace.class_data?.['N15_DTL_DEPRECIATION_DIFF'];
  const noteValue = !isExpenseNote ? depDiffClass?.amount_actual ?? null : null;

  return (
    <div style={stripStyle}>
      <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>ℹ</span>
      <span style={{ color: 'var(--text-primary)' }}>
        Depreciation Calc {isExpenseNote
          ? <>suggests a DT <b>charge/(credit) for the year of {fmtBracket(calcValue)}</b></>
          : <>computes a <b>closing DTA/(DTL) of {fmtBracket(calcValue)}</b> on the depreciation timing difference
            {noteValue != null && <> — this note's Depreciation Difference class carries {fmt(noteValue)}</>}</>}
        {suggestion.taxRate != null && <span style={{ color: 'var(--text-secondary)' }}> · tax rate {suggestion.taxRate}%</span>}
      </span>
      <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>
        Working-paper cross-check (positive = DTA, liability in brackets) — informational only.
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// styles
// ---------------------------------------------------------------------------

const stripStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  margin: '0 12px 8px',
  padding: '8px 12px',
  border: '1px solid var(--border-color)',
  borderRadius: 8,
  background: 'var(--bg-secondary)',
  fontSize: '0.78rem',
};
const diffThStyle: React.CSSProperties = {
  padding: '3px 8px',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: 'var(--text-secondary)',
};
const diffTdStyle: React.CSSProperties = {
  padding: '3px 8px',
  borderBottom: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
};

export default DepCalcSuggestionStrip;
